import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Video, AudioFeatures } from '@meradio/shared-types';
import { parseTrackTitle, ParsedTrackTitle, formatKeyShort } from '@meradio/music-utils';
import { AudioWaveform, Clock, ExternalLink, FolderMinus, FolderPlus, ListPlus, Loader2, MoreVertical, Music2, Play } from 'lucide-react';
import { IconButton, ActionSheet, Tooltip } from '@/components/ui';
import type { ActionSheetAction } from '@/components/ui';
import { LikeButton } from '@/components/likes';
import { getAudioFeaturesForVideo } from '@/services/audio.api';
import { startAnalyzing, stopAnalyzing } from '@/features/videos/videosSlice';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { selectLikedVideos, toggleVideoLike } from '@/features/videos/videosSlice';
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice';
import { brand } from '@/theme/colors';

interface VideoWithChannel extends Video {
  channel?: {
    id?: string;
    channelId?: string;
    name?: string;
    thumbnailUrl?: string;
  };
}

interface VideoTableProps {
  videos: VideoWithChannel[];
  audioFeatures: Record<string, AudioFeatures>;
  onPlayVideo: (video: VideoWithChannel) => void;
  onAddToQueue: (video: VideoWithChannel) => void;
  onAnalyze?: (video: VideoWithChannel) => void;
  onAddToCrate?: (video: VideoWithChannel) => void;
  onRemoveFromCrate?: (video: VideoWithChannel) => void;
  className?: string;
}

type SortKey = 'title' | 'artist' | 'publishedAt' | 'bpm' | 'key';
type SortDirection = 'asc' | 'desc';

const VideoTable: React.FC<VideoTableProps> = ({
  videos,
  audioFeatures,
  onPlayVideo,
  onAddToQueue,
  onAnalyze,
  onAddToCrate,
  onRemoveFromCrate,
  className = '',
}) => {
  const [sortKey, setSortKey] = useState<SortKey>('publishedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [sheetVideo, setSheetVideo] = useState<VideoWithChannel | null>(null);
  const currentVideoId = useAppSelector(state => state.player.currentVideo?.videoId);
  const isPlaying = useAppSelector(state => state.player.isPlaying);
  const likedVideos = useAppSelector(selectLikedVideos);
  const dispatch = useAppDispatch();
  const analyzing = useAppSelector(state => state.videos.analyzing);
  const [resolvedFeatures, setResolvedFeatures] = useState<Record<string, AudioFeatures>>({});
  const analyzingIds = useMemo(() => new Set(Object.keys(analyzing)), [analyzing]);

  const startAnalysis = (video: VideoWithChannel) => {
    if (!onAnalyze) return;
    onAnalyze(video);
    dispatch(startAnalyzing({ videoId: video.videoId, startedAt: Date.now() }));
  };

  useEffect(() => {
    if (analyzingIds.size === 0) return;
    const interval = setInterval(async () => {
      const pending = videos.filter(v => analyzingIds.has(v.videoId));
      for (const video of pending) {
        if (Date.now() - (analyzing[video.videoId] ?? 0) > 10 * 60 * 1000) {
          dispatch(stopAnalyzing(video.videoId));
          continue;
        }
        const found = await getAudioFeaturesForVideo(video.id);
        if (found && (found as any).tempo !== undefined) {
          setResolvedFeatures(prev => ({ ...prev, [video.videoId]: found as AudioFeatures }));
          dispatch(stopAnalyzing(video.videoId));
        }
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [analyzingIds, analyzing, videos, dispatch]);

  const isTouch = useIsTouchDevice();

  const parsedTitles = useMemo(() => {
    const map = new Map<string, ParsedTrackTitle>();
    videos.forEach(video => map.set(video.id, parseTrackTitle(video.title)));
    return map;
  }, [videos]);
  const parsed = (video: VideoWithChannel): ParsedTrackTitle =>
    parsedTitles.get(video.id) ?? { track: video.title };

  const quietLink = 'hover:text-meradio-primary hover:underline';
  const stopRowClick = (e: React.MouseEvent) => e.stopPropagation();

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  const sortedVideos = [...videos].sort((a, b) => {
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    
    switch (sortKey) {
      case 'title':
        return multiplier * parsed(a).track.localeCompare(parsed(b).track);

      case 'artist':
        return multiplier * (parsed(a).artist ?? '').localeCompare(parsed(b).artist ?? '');
      
      case 'publishedAt':
        return multiplier * (new Date(a.publishedAt).getTime() - new Date(b.publishedAt).getTime());
      
      case 'bpm': {
        const bpmA = audioFeatures[a.videoId]?.tempo || 0;
        const bpmB = audioFeatures[b.videoId]?.tempo || 0;
        return multiplier * (bpmA - bpmB);
      }
      
      case 'key': {
        const keyA = audioFeatures[a.videoId]?.key || 0;
        const keyB = audioFeatures[b.videoId]?.key || 0;
        return multiplier * (keyA - keyB);
      }
      
      default:
        return 0;
    }
  });

  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - dateObj.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const getYouTubeUrl = (videoId: string) => `https://www.youtube.com/watch?v=${videoId}`;

  const sheetFeatures = sheetVideo ? audioFeatures[sheetVideo.videoId] : undefined;
  const sheetActions: ActionSheetAction[] = sheetVideo
    ? [
        {
          label: 'Add to queue',
          icon: <ListPlus className="w-5 h-5" />,
          onSelect: () => onAddToQueue(sheetVideo),
        },
        ...(onAddToCrate
          ? [
              {
                label: 'Add to crate',
                icon: <FolderPlus className="w-5 h-5" />,
                onSelect: () => onAddToCrate(sheetVideo),
              },
            ]
          : []),
        ...(onRemoveFromCrate
          ? [
              {
                label: 'Remove from crate',
                icon: <FolderMinus className="w-5 h-5" />,
                onSelect: () => onRemoveFromCrate(sheetVideo),
              },
            ]
          : []),
        ...(onAnalyze && !sheetFeatures
          ? [
              {
                label: 'Analyze BPM and key',
                icon: <AudioWaveform className="w-5 h-5" />,
                onSelect: () => startAnalysis(sheetVideo),
              },
            ]
          : []),
        {
          label: 'Open in YouTube',
          icon: <ExternalLink className="w-5 h-5" />,
          onSelect: () =>
            window.open(getYouTubeUrl(sheetVideo.videoId), '_blank', 'noopener,noreferrer'),
        },
      ]
    : [];

  const SortableHeader: React.FC<{ column: SortKey; children: React.ReactNode; className?: string }> = ({ 
    column, 
    children,
    className: headerClass = '' 
  }) => (
    <th
      onClick={() => handleSort(column)}
      className={`px-4 py-3 text-left text-sm font-medium text-meradio-dark-light dark:text-text-dark-secondary uppercase tracking-wider cursor-pointer hover:text-meradio-primary transition-colors ${headerClass}`}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortKey === column && (
          <span className="text-meradio-primary">
            {sortDirection === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </th>
  );

  return (
    <div className={`bg-white dark:bg-bg-dark-secondary md:rounded-xl md:shadow-sm md:border border-meradio-light dark:border-border-dark overflow-hidden ${className}`}>
      <div className="w-full">
        <table className="w-full divide-y divide-meradio-light dark:divide-border-dark table-fixed">
          <thead className="bg-meradio-light/30 dark:bg-bg-dark-tertiary">
            <tr>
              <th className="hidden md:table-cell px-2 py-3 text-left text-sm font-medium text-meradio-dark-light dark:text-text-dark-secondary uppercase tracking-wider w-12">
                #
              </th>
              <SortableHeader column="title" className="md:w-[30%]">
                Track
              </SortableHeader>
              <SortableHeader column="artist" className="w-[16%] hidden md:table-cell">
                Artist
              </SortableHeader>
              <th className="px-2 py-3 text-left text-sm font-medium text-meradio-dark-light dark:text-text-dark-secondary uppercase tracking-wider w-[14%] hidden lg:table-cell">
                Album
              </th>
              <SortableHeader column="bpm" className="w-20 hidden lg:table-cell">
                BPM
              </SortableHeader>
              <SortableHeader column="key" className="w-16 hidden lg:table-cell">
                Key
              </SortableHeader>
              <SortableHeader column="publishedAt" className="w-28 hidden xl:table-cell">
                Published
              </SortableHeader>
              <th className="px-2 py-3 text-right text-sm font-medium text-meradio-dark-light dark:text-text-dark-secondary uppercase tracking-wider w-24">
                <Clock className="w-4 h-4 inline" />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-meradio-light dark:divide-border-dark">
            {sortedVideos.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center">
                  <Music2 className="w-12 h-12 text-meradio-dark-light dark:text-text-dark-tertiary mx-auto mb-3" />
                  <p className="text-meradio-dark-light dark:text-text-dark-secondary">
                    No videos found
                  </p>
                </td>
              </tr>
            ) : (
              sortedVideos.map((video, index) => {
                const features = audioFeatures[video.videoId] ?? resolvedFeatures[video.videoId];
                const isHovered = hoveredRow === video.videoId;
                const isCurrent = currentVideoId === video.videoId;
                
                return (
                  <tr
                    key={video.videoId}
                    onMouseEnter={() => setHoveredRow(video.videoId)}
                    onMouseLeave={() => setHoveredRow(null)}
                    className="group hover:bg-meradio-light/20 dark:hover:bg-bg-dark-tertiary/50 transition-colors cursor-pointer"
                  >
                    <td className="hidden md:table-cell px-2 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-center">
                        {isHovered && !isCurrent && !isTouch ? (
                          <Tooltip label="Play now">
                            <IconButton
                              onClick={() => onPlayVideo(video)}
                              variant="solid"
                              size="sm"
                              aria-label="Play now"
                            >
                              <Play className="w-4 h-4 fill-current" />
                            </IconButton>
                          </Tooltip>
                        ) : isCurrent ? (
                          <Tooltip label={isPlaying ? 'Now playing' : 'Paused'}>
                            <div
                              data-testid="now-playing-vinyl"
                              className={`relative w-7 h-7 rounded-full ${isPlaying ? 'spinning animate-spin-slow' : ''}`}
                              style={{
                                background: `radial-gradient(circle at 30% 30%, ${brand.dark} 0%, ${brand.darkLight} 40%, ${brand.dark} 70%, #000 100%)`,
                              }}
                              aria-label={isPlaying ? 'Now playing' : 'Paused'}
                            >
                              <div className="absolute inset-0.5 rounded-full border border-neutral-600 opacity-30" />
                              <div className="absolute inset-1 rounded-full border border-neutral-600 opacity-20" />
                              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-meradio-primary rounded-full flex items-center justify-center shadow-inner">
                                <div className="w-1 h-1 bg-meradio-dark rounded-full" />
                              </div>
                            </div>
                          </Tooltip>
                        ) : (
                          <span className="text-sm text-meradio-dark-light dark:text-text-dark-tertiary">
                            {index + 1}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="px-2 py-3" onClick={() => onPlayVideo(video)}>
                      <div className="flex items-center space-x-3">
                        <div className="relative w-12 h-12 flex-shrink-0 rounded overflow-hidden bg-meradio-light dark:bg-bg-dark-primary">
                          {isCurrent && (
                            <div className="md:hidden absolute inset-0 z-10 bg-black/60 flex items-center justify-center">
                              <div
                                className={`relative w-6 h-6 rounded-full ${isPlaying ? 'animate-spin-slow' : ''}`}
                                style={{
                                  background: `radial-gradient(circle at 30% 30%, ${brand.dark} 0%, ${brand.darkLight} 40%, ${brand.dark} 70%, #000 100%)`,
                                }}
                              >
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-meradio-primary rounded-full" />
                              </div>
                            </div>
                          )}
                          {video.thumbnailUrl ? (
                            <img
                              src={video.thumbnailUrl}
                              alt={video.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Music2 className="w-6 h-6 text-meradio-dark-light dark:text-text-dark-tertiary" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-meradio-dark dark:text-text-dark-primary truncate">
                            {analyzingIds.has(video.videoId) && (
                              <Loader2 className="lg:hidden inline w-3 h-3 mr-1 text-meradio-primary animate-spin" />
                            )}
                            {parsed(video).track}
                          </p>
                          {parsed(video).artist && (
                            <p className="md:hidden text-xs text-meradio-dark-light dark:text-text-dark-secondary truncate">
                              <Link
                                to={`/artists/${encodeURIComponent(parsed(video).artist!)}`}
                                onClick={stopRowClick}
                                className={quietLink}
                              >
                                {parsed(video).artist}
                              </Link>
                            </p>
                          )}
                          {parsed(video).album && (
                            <p className="md:hidden text-xs text-meradio-dark-light dark:text-text-dark-tertiary truncate">
                              <Link
                                to={`/albums/${encodeURIComponent(parsed(video).album!)}`}
                                onClick={stopRowClick}
                                className={quietLink}
                              >
                                {parsed(video).album}
                              </Link>
                            </p>
                          )}
                          {video.channel?.name && (
                            <p className="text-xs text-meradio-dark-light dark:text-text-dark-tertiary truncate">
                              {video.channel.channelId ? (
                                <Link
                                  to={`/channels/${video.channel.channelId}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="hover:text-meradio-primary hover:underline transition-colors"
                                >
                                  {video.channel.name}
                                </Link>
                              ) : (
                                video.channel.name
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-2 py-3 whitespace-nowrap hidden md:table-cell">
                      <p className="text-sm text-meradio-dark-light dark:text-text-dark-secondary truncate">
                        {parsed(video).artist ? (
                          <Link
                            to={`/artists/${encodeURIComponent(parsed(video).artist!)}`}
                            onClick={stopRowClick}
                            className={quietLink}
                          >
                            {parsed(video).artist}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </p>
                    </td>

                    <td className="px-2 py-3 whitespace-nowrap hidden lg:table-cell">
                      <p className="text-sm text-meradio-dark-light dark:text-text-dark-secondary truncate">
                        {parsed(video).album ? (
                          <Link
                            to={`/albums/${encodeURIComponent(parsed(video).album!)}`}
                            onClick={stopRowClick}
                            className={quietLink}
                          >
                            {parsed(video).album}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </p>
                    </td>

                    <td className="px-2 py-3 whitespace-nowrap hidden lg:table-cell">
                      {features ? (
                        <span className="text-sm text-meradio-dark dark:text-text-dark-secondary">
                          {Math.round(features.tempo)}
                        </span>
                      ) : analyzingIds.has(video.videoId) ? (
                        <Tooltip label="Analyzing — values appear when done">
                          <Loader2
                            data-testid={`analyzing-${video.videoId}`}
                            aria-label="Analysis running"
                            className="w-4 h-4 text-meradio-primary animate-spin"
                          />
                        </Tooltip>
                      ) : onAnalyze ? (
                        <Tooltip label="Analyze BPM and key">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              startAnalysis(video);
                            }}
                            aria-label="Analyze BPM and key"
                            className="text-meradio-dark-light dark:text-text-dark-tertiary hover:text-meradio-primary transition-colors"
                          >
                            <AudioWaveform className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      ) : (
                        <span className="text-sm text-meradio-dark dark:text-text-dark-secondary">—</span>
                      )}
                    </td>

                    <td className="px-2 py-3 whitespace-nowrap hidden lg:table-cell">
                      {features ? (
                        <span className="text-sm text-meradio-dark dark:text-text-dark-secondary">
                          {formatKeyShort(features.key, features.mode)}
                        </span>
                      ) : analyzingIds.has(video.videoId) ? (
                        <Tooltip label="Analyzing — values appear when done">
                          <Loader2 className="w-4 h-4 text-meradio-primary animate-spin" />
                        </Tooltip>
                      ) : onAnalyze ? (
                        <Tooltip label="Analyze BPM and key">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              startAnalysis(video);
                            }}
                            aria-label="Analyze BPM and key"
                            className="text-meradio-dark-light dark:text-text-dark-tertiary hover:text-meradio-primary transition-colors"
                          >
                            <AudioWaveform className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      ) : (
                        <span className="text-sm text-meradio-dark dark:text-text-dark-secondary">—</span>
                      )}
                    </td>

                    <td className="px-2 py-3 whitespace-nowrap hidden xl:table-cell">
                      <span className="text-sm text-meradio-dark-light dark:text-text-dark-secondary">
                        {formatDate(video.publishedAt)}
                      </span>
                    </td>

                    <td className="px-2 py-3 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-1">
                        {isTouch ? (
                          <Tooltip label="More actions">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                setSheetVideo(video);
                              }}
                              variant="ghost"
                              size="sm"
                              aria-label="More actions"
                              className="text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-primary"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                        <div
                          className={`flex items-center space-x-1 transition-opacity duration-150 ${
                            isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
                          }`}
                        >
                          <Tooltip label="Add to queue">
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                onAddToQueue(video);
                              }}
                              variant="ghost"
                              size="sm"
                              aria-label="Add to queue"
                              tabIndex={isHovered ? 0 : -1}
                              className="text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-primary"
                            >
                              <ListPlus className="w-4 h-4" />
                            </IconButton>
                          </Tooltip>


                          {onAddToCrate && (
                            <Tooltip label="Add to crate">
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAddToCrate(video);
                                }}
                                variant="ghost"
                                size="sm"
                                aria-label="Add to crate"
                                tabIndex={isHovered ? 0 : -1}
                                className="text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-primary"
                              >
                                <FolderPlus className="w-4 h-4" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {onRemoveFromCrate && (
                            <Tooltip label="Remove from crate">
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRemoveFromCrate(video);
                                }}
                                variant="ghost"
                                size="sm"
                                aria-label="Remove from crate"
                                tabIndex={isHovered ? 0 : -1}
                                className="text-meradio-dark-light dark:text-text-dark-secondary hover:text-danger-600"
                              >
                                <FolderMinus className="w-4 h-4" />
                              </IconButton>
                            </Tooltip>
                          )}

                          <Tooltip label="Open in YouTube">
                            <a
                              href={getYouTubeUrl(video.videoId)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-meradio-dark-light dark:text-text-dark-secondary hover:text-meradio-primary transition-colors"
                              tabIndex={isHovered ? 0 : -1}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </Tooltip>
                        </div>
                        )}

                        <LikeButton
                          videoId={video.id}
                          initialLiked={(video as any).isLiked ?? likedVideos.includes(video.id)}
                          onLikeChange={liked => dispatch(toggleVideoLike({ videoId: video.id, liked }))}
                          size="small"
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ActionSheet
        open={sheetVideo !== null}
        onClose={() => setSheetVideo(null)}
        title={sheetVideo ? parsed(sheetVideo).track : undefined}
        actions={sheetActions}
      />
    </div>
  );
};

export default VideoTable;
